/**
 * The head a page renders itself (web plan W5.1, replacing W0's `useSeo`
 * hook): what `<PageHead/>` (@/components/seo/PageHead) puts in the document
 * after an in-app navigation, and on every render without the SEO server
 * (`react-router dev`, the component tests).
 *
 * On the URL the document was served for, the SEO server's head stands (the
 * root's and the route modules' `meta`, @/seo/documentHead) and the page
 * renders none of this: the server already built the same head from the same
 * `@/seo/pageHeads` functions, and its robots and canonical are final there.
 *
 * Pure module: no `window`, no React.
 */
import type { Translate } from '@/i18n';
import type { Locale } from '@/seo/locales';
import { NOINDEX_ROBOTS, robotsValue } from '@/seo/lifecycle';
import { staticSeoHeadFor } from '@/seo/staticHeads';
import {
  DEFAULT_OG_IMAGE_PATH,
  ogImageSize,
  siteDefaults,
  SITE_NAME,
  titledPageTitle,
  type SeoHead,
} from '@/seo/templates';
import { absoluteUrl, canonicalFor } from '@/seo/urls';

/** `og:type` is `website` on every page (web plan W0.7), as the heads in `@/seo/templates` say. */
export type SeoType = 'website';

export interface PageHeadOptions {
  /** Page title without the brand suffix. Omit for the bare brand title. */
  title?: string | null;
  /** Meta description (~50–160 chars). Falls back to the site description. */
  description?: string | null;
  /** Absolute image URL for OG / Twitter cards. Falls back to the OG banner. */
  image?: string | null;
  /**
   * Canonical path, normalised by `canonicalFor` (lowercase, no trailing
   * slash). Defaults to the current pathname with the whole query dropped; a
   * paginated list opts in with `page` (or its own `?page=N` here; N ≥ 2 is kept).
   */
  canonicalPath?: string;
  /**
   * 1-based page of a paginated list (web plan W0.11): N ≥ 2 adds " – Page N"
   * to the title and keeps `?page=N` in the canonical, so every page is its
   * own self-canonical URL; page 1 is the bare path. A `head` built with the
   * same `{ page }` already carries both, and nothing is added twice.
   */
  page?: number | null;
  /** OpenGraph object type. */
  type?: SeoType;
  /** Keep the page out of search indexes (auth, account, 404, …). Always wins. */
  noindex?: boolean;
  /**
   * Write no robots tag: a detail page that is still loading, or whose load
   * failed (429 / 5xx / network), does not know its robots yet (web plan W0.2
   * — client errors never downgrade robots). `noindex: true` still wins.
   */
  keepRobots?: boolean;
  /**
   * Extra directives appended to the index robots value, e.g. a promotion's
   * `unavailable_after: 2026-10-07T23:59:59+05:00` (`promotionLifecycle()`
   * in `@/seo/lifecycle`). Ignored with `noindex` and with `head`, whose
   * robots already carry them.
   */
  robotsExtra?: string | null;
  /** JSON-LD structured data object(s), rendered as the page's one JSON-LD script. */
  jsonLd?: object | object[] | null;
  /**
   * A complete head from `@/seo/templates`: its title is used verbatim, and it
   * supplies the description, canonical, robots, image and JSON-LD. Options
   * set alongside it win over its fields (`noindex: true` always does).
   * Without one, a fixed route of `@/seo/staticHeads` uses its prerendered
   * head (web plan W0.18), so the page never contradicts that file.
   */
  head?: SeoHead | null;
}

/** Everything `<PageHead/>` renders. */
export interface ClientHead {
  title: string;
  description: string;
  /** Null: no robots tag (`keepRobots`). */
  robots: string | null;
  /** Absolute, on SITE_URL. */
  canonical: string;
  type: SeoType;
  image: string;
  imageSize: { width: number; height: number } | null;
  imageAlt: string;
  /** The page's other-country versions (hreflang), from its head; empty without one. */
  alternates?: ReadonlyArray<{ hreflang: string; url: string }>;
  /** The JSON-LD document (`@context` added), or null for none. */
  jsonLd: Record<string, unknown> | null;
}

/** A list page worth naming: a whole N ≥ 2 (page 1 is the list itself). */
function listPage(page: number | null | undefined): number | null {
  return typeof page === 'number' && Number.isSafeInteger(page) && page >= 2 ? page : null;
}

/** "{title} – Page N" (the catalogue's `common:title.page`), before a head title's " | PromoStork" suffix. */
function withPage(fullTitle: string, page: number, t: Translate): string {
  const suffix = ` | ${SITE_NAME}`;
  return fullTitle.endsWith(suffix)
    ? `${t('common:title.page', { title: fullTitle.slice(0, -suffix.length), page })}${suffix}`
    : t('common:title.page', { title: fullTitle, page });
}

function clamp(text: string, max = 160): string {
  const t = text.replace(/\s+/g, ' ').trim();
  if (t.length <= max) return t;
  return `${t.slice(0, max - 1).trimEnd()}…`;
}

/** Nodes as one JSON-LD document: a single node keeps its shape, several go in an `@graph`. */
export function jsonLdDocument(data: object | object[] | null | undefined): Record<string, unknown> | null {
  if (!data) return null;
  return Array.isArray(data)
    ? { '@context': 'https://schema.org', '@graph': data }
    : { '@context': 'https://schema.org', ...data };
}

/** The head for `options` on `pathname`, in `locale`'s words (see the file comment). */
export function clientHead(
  options: PageHeadOptions,
  { pathname, locale, t }: { pathname: string; locale: Locale; t: Translate },
): ClientHead {
  const {
    title,
    description,
    image,
    canonicalPath,
    page,
    type = 'website',
    noindex = false,
    keepRobots = false,
    robotsExtra,
    jsonLd,
    head: pageHead,
  } = options;
  const pageNumber = listPage(page);

  // The page's own head, or on a fixed route (lists, sale pages, legal
  // pages) the head prerendered into its dist/<route>/index.html (web plan
  // W0.18), so the title and card never change when the page takes over.
  const head = pageHead ?? staticSeoHeadFor(pathname);

  // A head built with `{ page }` already names its page in the title and
  // canonical (`?page=N`); `page` only adds what is still missing.
  const headPaged = Boolean(head && /[?&]page=\d/.test(head.canonicalPath));
  const defaults = siteDefaults({ locale, t });
  const fullTitle =
    title && title.trim()
      ? titledPageTitle(title, pageNumber, t)
      : head?.title
        ? pageNumber && !headPaged
          ? withPage(head.title, pageNumber, t)
          : head.title
        : defaults.title;
  const desc = clamp(description?.trim() || head?.description || defaults.description);
  // Always on SITE_URL, never the current host: www.promostork.com (or any
  // other host serving this bundle) must canonicalise to the apex. The
  // location's query is ignored so an unpaginated page reached with
  // `?page=3` does not declare itself a separate page; a paginated list
  // passes `page` (or `?page=N` in `canonicalPath`; N ≥ 2 is kept,
  // `?page=1` dropped), so each of its pages is self-canonical.
  let canonicalPathname = canonicalFor(canonicalPath ?? head?.canonicalPath ?? pathname);
  if (pageNumber && !canonicalPathname.includes('?')) {
    canonicalPathname = `${canonicalPathname}?page=${pageNumber}`;
  }
  const defaultImage = absoluteUrl(DEFAULT_OG_IMAGE_PATH);
  const ogImage = image || head?.og.image || defaultImage;
  // The alt describes the image that is actually shown: the site image is
  // the logo; a head's own image keeps the head's alt unless the page
  // overrides the title; any other image is described by the title.
  const imageAlt =
    ogImage === defaultImage
      ? defaults.imageAlt
      : !image && !(title && title.trim()) && head
        ? head.og.imageAlt
        : fullTitle;
  // noindex always wins; a head's robots are exact (D-4 lifecycle or the
  // backend's seo.robots); otherwise the index base plus any extras.
  // `keepRobots` (loading / failed detail loads) writes none.
  const robots =
    noindex ? NOINDEX_ROBOTS : keepRobots ? null : head ? head.robots : robotsValue({ extra: robotsExtra });
  return {
    title: fullTitle,
    description: desc,
    robots,
    canonical: absoluteUrl(canonicalPathname),
    type,
    image: ogImage,
    // Prefer the size stored on the head (e.g. 1200×630 for a backend share
    // card); otherwise fall back to URL-based detection (the static site image
    // is the only other known-size case). A CDN crop gets no size tags so chat
    // apps measure it themselves rather than trust a potentially wrong value.
    imageSize: head?.og.imageSize ?? ogImageSize(ogImage) ?? null,
    imageAlt,
    ...(!noindex && head?.alternates?.length ? { alternates: head.alternates } : {}),
    jsonLd: jsonLdDocument(jsonLd ?? (head?.jsonLd.length ? head.jsonLd : null)),
  };
}
